import type { QueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import type {
  CalendarMonthPlanWithSlots,
  CalendarPlanSlot,
  CalendarSlotIdea,
  CalendarSlotStatus,
} from "@/types/contentCalendar.type";

/**
 * Cache surgery for the month plan — the single entry every calendar surface
 * reads from.
 *
 * The ideation endpoints have already staked their claim by the time they
 * answer: the seat (or the one idea) is moved to its working state in the same
 * transaction, and the whole updated slot comes back in the body. So a click
 * never needs a re-fetch to learn its own outcome — the UI applies the move the
 * server is about to confirm, then reconciles against the response. Invalidating
 * instead is what made the board blank out and re-appear on every click.
 *
 * The pitch cards, the stage rail, the month grid and the poll interval are all
 * derived from this entry, so one write moves them together.
 */

export const planKey = (planId: string | null) => ["calendarPlan", planId];

/**
 * Keep an in-flight poll from landing on top of an optimistic write and
 * momentarily reverting it. Always await this before patching.
 */
export function cancelPlanFetches(qc: QueryClient, planId: string | null) {
  return qc.cancelQueries({ queryKey: planKey(planId) });
}

// ---------------------------------------------------------------- write gate
//
// A read of the month that overlaps a write may carry the month from before
// it. Writing that over the optimistic state is what made cards snap back:
// a poll or focus refetch that started after the click, a second drag's
// refetch dropping the first, a send's whole-month reply wiping a delete still
// in flight. So every mutation that patches a month carries `planWriteKey` at
// the head of its mutationKey, reads that overlap one keep the cache, and only
// the last write to settle touches the month again. The distribution panel's
// `wroteSince` / `settleWrite` (useScheduling) is the same rule for one post.

/** Head of the mutationKey of every write that patches a month. */
export const planWriteKey = (planId: string | null) => ["calendarWrite", planId];

/** Months whose refresh was held back while a write ran; the last write to settle refetches them. */
const dirtyPlans = new Set<string>();

function pendingPlanWrites(qc: QueryClient, planId: string): number {
  return qc.isMutating({ mutationKey: planWriteKey(planId) });
}

/** True when a write on the month is still running, or started after `since`. */
function planWroteSince(qc: QueryClient, planId: string, since: number): boolean {
  return qc
    .getMutationCache()
    .findAll({ mutationKey: planWriteKey(planId) })
    .some((mutation) => mutation.state.status === "pending" || mutation.state.submittedAt >= since);
}

/**
 * Refetch the month, unless a write on it is running: then the refresh is held
 * and the last write to settle does it, so it never lands on top of an
 * optimistic change. Realtime hints and agent tools refresh through here.
 */
export function reconcilePlan(qc: QueryClient, planId: string | null) {
  if (!planId) return;
  if (pendingPlanWrites(qc, planId) > 0) {
    dirtyPlans.add(planId);
    return;
  }
  dirtyPlans.delete(planId);
  void qc.invalidateQueries({ queryKey: planKey(planId) });
}

/**
 * A month write is done (call it from `onSettled`, once). Only the last write
 * on the month to settle acts:
 *  - it lays the server's copy of its post over the cache;
 *  - it takes a whole-month reply only when nothing overlapped it, since that
 *    reply may predate another write's commit;
 *  - it refetches when it has no copy, when asked to (`refetch: true`, e.g.
 *    after a rollback), or when anything was held back while writes ran.
 *    `refetch: false` says the write already patched in the server's answer.
 * Earlier settlers only mark the month, so a reply never wipes a sibling's
 * optimistic change.
 */
export function settlePlanWrite(
  qc: QueryClient,
  planId: string | null,
  reply: { slot?: CalendarPlanSlot | null; plan?: CalendarMonthPlanWithSlots | null; refetch?: boolean } = {},
) {
  if (!planId) return;
  // The settling mutation still counts itself here, hence > 1.
  if (pendingPlanWrites(qc, planId) > 1) {
    dirtyPlans.add(planId);
    return;
  }
  const dirty = dirtyPlans.delete(planId);
  if (reply.slot) putSlot(qc, planId, reply.slot);
  else if (reply.plan && !dirty) putPlan(qc, planId, reply.plan);
  const refetch = reply.refetch ?? (!reply.slot && !reply.plan);
  if (dirty || refetch) void qc.invalidateQueries({ queryKey: planKey(planId) });
}

/** `reconcilePlan` for every month in the cache: writes that may land in any month (publishing, agent tools). */
export function reconcileAllPlans(qc: QueryClient) {
  for (const query of qc.getQueryCache().findAll({ queryKey: ["calendarPlan"] })) {
    const planId = query.queryKey[1];
    if (typeof planId === "string") reconcilePlan(qc, planId);
  }
}

/**
 * The month query, defined once for every reader (the open month, the other
 * months, Home), so its fetch rule never depends on which mounted first.
 * A read that overlapped a write keeps the cached month; the write's settle
 * (or a refetch right after, when it already settled) brings the server's.
 */
export function planQueryOptions(qc: QueryClient, planId: string | null) {
  return {
    queryKey: planKey(planId),
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<CalendarMonthPlanWithSlots> => {
      const started = Date.now();
      // The signal lets a superseded read (a settle's refetch cancels it) stop here.
      const res = await axiosInstance.get<{ plan: CalendarMonthPlanWithSlots }>(
        `/content-calendar/plans/${planId}`,
        { signal },
      );
      const cached = readPlan(qc, planId);
      if (planId && cached && planWroteSince(qc, planId, started)) {
        if (pendingPlanWrites(qc, planId) > 0) dirtyPlans.add(planId);
        else setTimeout(() => reconcilePlan(qc, planId), 0);
        return cached;
      }
      return res.data.plan;
    },
    enabled: !!planId,
  };
}

/** The whole month — the rollback snapshot for a write that moves several posts. */
export function readPlan(
  qc: QueryClient,
  planId: string | null,
): CalendarMonthPlanWithSlots | undefined {
  return qc.getQueryData<CalendarMonthPlanWithSlots>(planKey(planId));
}

export function putPlan(
  qc: QueryClient,
  planId: string | null,
  plan: CalendarMonthPlanWithSlots,
) {
  qc.setQueryData<CalendarMonthPlanWithSlots>(planKey(planId), plan);
}

export function readSlot(
  qc: QueryClient,
  planId: string | null,
  slotId: string,
): CalendarPlanSlot | undefined {
  return readPlan(qc, planId)?.slots.find((s) => s.id === slotId);
}

export function readIdea(
  qc: QueryClient,
  planId: string | null,
  slotId: string,
  ideaId: string,
): CalendarSlotIdea | undefined {
  return readSlot(qc, planId, slotId)?.ideas?.find((i) => i.id === ideaId);
}

/** Replace one slot wholesale — used both for the server's copy and for rollback. */
export function putSlot(qc: QueryClient, planId: string | null, slot: CalendarPlanSlot) {
  qc.setQueryData<CalendarMonthPlanWithSlots>(planKey(planId), (plan) =>
    plan ? { ...plan, slots: plan.slots.map((s) => (s.id === slot.id ? slot : s)) } : plan,
  );
}

export type RemovedSlot = { slot: CalendarPlanSlot; index: number };

/**
 * Take a post off the month, optimistically. Returns where it sat so a failed
 * delete puts back just this post: a whole-month snapshot would also resurrect
 * posts deleted alongside it (bulk delete fires one request per post).
 */
export function removeSlotFromCache(
  qc: QueryClient,
  planId: string | null,
  slotId: string,
): RemovedSlot | undefined {
  const plan = readPlan(qc, planId);
  const index = plan ? plan.slots.findIndex((s) => s.id === slotId) : -1;
  if (!plan || index < 0) return undefined;
  putPlan(qc, planId, { ...plan, slots: plan.slots.filter((s) => s.id !== slotId) });
  return { slot: plan.slots[index], index };
}

/** Undo `removeSlotFromCache`; a no-op if a refetch already brought the post back. */
export function restoreSlotToCache(qc: QueryClient, planId: string | null, removed: RemovedSlot) {
  qc.setQueryData<CalendarMonthPlanWithSlots>(planKey(planId), (plan) => {
    if (!plan || plan.slots.some((s) => s.id === removed.slot.id)) return plan;
    const slots = [...plan.slots];
    slots.splice(Math.min(removed.index, slots.length), 0, removed.slot);
    return { ...plan, slots };
  });
}

/** Edit one slot in place, leaving the rest of the month untouched. */
export function patchSlot(
  qc: QueryClient,
  planId: string | null,
  slotId: string,
  edit: (slot: CalendarPlanSlot) => CalendarPlanSlot,
) {
  qc.setQueryData<CalendarMonthPlanWithSlots>(planKey(planId), (plan) =>
    plan ? { ...plan, slots: plan.slots.map((s) => (s.id === slotId ? edit(s) : s)) } : plan,
  );
}

/**
 * Edit every slot the predicate picks — one pass for a write that moves a set
 * of posts together (sharing a batch, pulling one back). Applying `patchSlot`
 * per id would rewrite the month array once per post and re-render between.
 */
export function patchSlotsWhere(
  qc: QueryClient,
  planId: string | null,
  match: (slot: CalendarPlanSlot) => boolean,
  edit: (slot: CalendarPlanSlot) => CalendarPlanSlot,
) {
  qc.setQueryData<CalendarMonthPlanWithSlots>(planKey(planId), (plan) =>
    plan ? { ...plan, slots: plan.slots.map((s) => (match(s) ? edit(s) : s)) } : plan,
  );
}

/**
 * Land a slot on another day, optimistically — the mirror of the server's
 * `moveToDay`. A dragged card has to appear where it was dropped on the frame
 * the mouse comes up, so this applies the whole outcome locally: the new day,
 * the renumbered `order` run of the destination day, and the one status flip a
 * move can cause (see `statusAfterMove` in slotStage).
 *
 * `position` is an insert index within the target day; omitted means last.
 */
export function moveSlotInCache(
  qc: QueryClient,
  planId: string | null,
  move: { slotId: string; scheduledAt: string; position?: number; status: CalendarSlotStatus },
) {
  qc.setQueryData<CalendarMonthPlanWithSlots>(planKey(planId), (plan) => {
    if (!plan) return plan;
    const moved = plan.slots.find((s) => s.id === move.slotId);
    if (!moved) return plan;

    const targetDay = new Date(move.scheduledAt).getTime();
    const dayMates = plan.slots
      .filter((s) => s.id !== move.slotId && new Date(s.scheduledAt).getTime() === targetDay)
      .sort((a, b) => a.order - b.order);
    const index = Math.min(Math.max(move.position ?? dayMates.length, 0), dayMates.length);
    const ordered = [
      ...dayMates.slice(0, index),
      { ...moved, scheduledAt: move.scheduledAt, status: move.status },
      ...dayMates.slice(index),
    ];
    const renumbered = new Map(ordered.map((slot, order) => [slot.id, { ...slot, order }]));

    return { ...plan, slots: plan.slots.map((s) => renumbered.get(s.id) ?? s) };
  });
}

/** Edit one idea in place, leaving its siblings alone. */
export function patchIdea(
  qc: QueryClient,
  planId: string | null,
  slotId: string,
  ideaId: string,
  edit: (idea: CalendarSlotIdea) => CalendarSlotIdea,
) {
  patchSlot(qc, planId, slotId, (slot) => ({
    ...slot,
    ideas: (slot.ideas ?? []).map((i) => (i.id === ideaId ? edit(i) : i)),
  }));
}
